import React, { useRef, FunctionComponent, useState, useMemo } from 'react'
import { Icon, Dropdown, Button } from '@afterpaytouch/core'
import { BankAccountDetails, CashAppPayDetail, isBankAccount, isCard } from '@afterpay/types'
import { useOutsideClickedCallback } from '../../../../utils/outsideClickedCallback'
import { Head } from '../../..'
import { CreditCard } from '@afterpaytouch/portal-api/consumer/account/creditcards/types'
import { useTranslation } from 'next-i18next'
import { PaymentMethodType } from '@afterpaytouch/portal-api/payments/types'
import { useFlag } from '../../../../hooks'
import { useRouter } from 'next/router'
import { useAmplitudeWithEnduringEventProperties } from '../../../../integrations/amplitude'
import { TrackingEvent } from '../../../../model/amplitude'

const I18N_NAMESPACE = ['common', 'account']

export interface PaymentMenuProps {
  paymentMethod: CreditCard | BankAccountDetails | CashAppPayDetail | PaymentMethodType.APPLE_PAY
  setSelectedPaymentMethod: (paymentMethod: CreditCard | BankAccountDetails | CashAppPayDetail | PaymentMethodType.APPLE_PAY) => void
  showOptionToRemove?: boolean
  showRemoveModal?: () => void
  showPreferredModal?: () => void
  showExistingOrderModal?: () => void
  showOneCardModal?: () => void
  showCardBillingAddressModal?: () => void
  showOptionToSetAsPreferred?: boolean
  showReportCardCancellation?: boolean
  showReportCardCancellationModal?: () => void
  numOfCardsOnAccount: number
  isBankAccountUnlinked?: boolean
  showRelinkModal?: () => void
  disabled?: boolean
  hasBillingAddress?: boolean
}

export const PaymentMenu: FunctionComponent<PaymentMenuProps> = ({
  paymentMethod,
  showOptionToRemove = true,
  showRemoveModal,
  showPreferredModal,
  showExistingOrderModal,
  showOneCardModal,
  showOptionToSetAsPreferred = true,
  setSelectedPaymentMethod,
  showCardBillingAddressModal,
  numOfCardsOnAccount,
  isBankAccountUnlinked = false,
  showRelinkModal,
  disabled = false,
  showReportCardCancellation = false,
  showReportCardCancellationModal,
  hasBillingAddress = false,
}) => {
  const [showMenu, setShowMenu] = useState<boolean>(false)
  const { t } = useTranslation(I18N_NAMESPACE)
  const isApplePay = paymentMethod === PaymentMethodType.APPLE_PAY

  const isBillingAddressFlagEnabled = useFlag('billing-address-details')
  const showBillingAddressItem = hasBillingAddress && isBillingAddressFlagEnabled && isCard(paymentMethod)

  const router = useRouter()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  // Handle close menu when clicking outside div
  const menuRef = useRef(null)
  const handleClose = (): void => {
    setShowMenu(false)
  }
  useOutsideClickedCallback(menuRef, handleClose)

  const handleRemove = (): void => {
    setSelectedPaymentMethod(paymentMethod)
    handleRemoveModal()
    handleClose()
  }

  const handleRemoveModal = (): void => {
    // @ts-ignore: OPERATION BLEED STOPPER
    if (!isApplePay && paymentMethod.numOfActiveOrders > 0) {
      // @ts-ignore: OPERATION BLEED STOPPER
      showExistingOrderModal()
    } else if (isCard(paymentMethod) && numOfCardsOnAccount === 1) {
      // @ts-ignore: OPERATION BLEED STOPPER
      showOneCardModal()
    } else {
      // @ts-ignore: OPERATION BLEED STOPPER
      showRemoveModal()
    }
  }

  const handlePreferred = (): void => {
    setSelectedPaymentMethod(paymentMethod)
    // @ts-ignore: OPERATION BLEED STOPPER
    showPreferredModal()
    handleClose()
  }

  const handleBillingAddress = (): void => {
    setSelectedPaymentMethod(paymentMethod)
    // @ts-ignore: OPERATION BLEED STOPPER
    showCardBillingAddressModal()
    handleClose()
    // @ts-ignore: OPERATION BLEED STOPPER
    logEvent(TrackingEvent.CLICKED_VIEW_BILLING_ADDRESS, { locale: router?.locale })
  }

  const handleRelink = (): void => {
    // @ts-ignore: OPERATION BLEED STOPPER
    showRelinkModal()
    handleClose()
  }

  const handleReportCardCancellation = (): void => {
    setSelectedPaymentMethod(paymentMethod)
    // @ts-ignore: OPERATION BLEED STOPPER
    showReportCardCancellationModal()
    handleClose()
  }

  const pageTitle = useMemo(() => `${t('account:tab:payment-methods:title')} - ${t('account:title')} | ${t('common:brandName')}`, [])

  return (
    <>
      <Head title={pageTitle} />
      <div className='relative' ref={menuRef}>
        <Button.Ghost
          disabled={disabled}
          size='Small'
          aria-label='more action'
          onClick={() => setShowMenu(!showMenu)}
          testNameSpace={isApplePay ? 'moreAction-applepay' : `moreAction-${paymentMethod.id}`}
        >
          <Icon.More size='ExtraSmall' />
        </Button.Ghost>
        {showMenu && (
          <div className='absolute right-0 z-10 mt-2 whitespace-nowrap'>
            <Dropdown.Menu>
              {!isApplePay && showOptionToRemove && (
                <Dropdown.MenuItem onClick={handleRemove} testNameSpace={`remove-${paymentMethod.id}`}>
                  {t('account:tab:payment-methods:body:paymentMethodCta:remove')}
                </Dropdown.MenuItem>
              )}
              {showBillingAddressItem && (
                <Dropdown.MenuItem onClick={handleBillingAddress} testNameSpace='credit-card-billing-address'>
                  {t('account:tab:payment-methods:body:paymentMethodCta:viewAddress')}
                </Dropdown.MenuItem>
              )}
              {showOptionToSetAsPreferred && showPreferredModal != null && (
                <Dropdown.MenuItem onClick={handlePreferred} testNameSpace={isApplePay ? 'preferred-applepay' : `preferred-${paymentMethod.id}`}>
                  {t('account:tab:payment-methods:body:paymentMethodCta:setPreferred')}
                </Dropdown.MenuItem>
              )}
              {isBankAccountUnlinked && isBankAccount(paymentMethod) && (
                <Dropdown.MenuItem onClick={handleRelink} testNameSpace={`relink-${paymentMethod.id}`}>
                  {t('account:tab:payment-methods:body:paymentMethodCta:relink')}
                </Dropdown.MenuItem>
              )}
              {showReportCardCancellation && (
                <Dropdown.MenuItem onClick={handleReportCardCancellation} testNameSpace='report-card-cancellation'>
                  {t('account:tab:payment-methods:body:paymentMethodCta:reportCardCancellation')}
                </Dropdown.MenuItem>
              )}
            </Dropdown.Menu>
          </div>
        )}
      </div>
    </>
  )
}
